import React, { useCallback, useEffect, useRef } from 'react';

import { datadogRum } from '@datadog/browser-rum';
import { useLocation } from 'react-router-dom';

export interface SLICtx {
    reportSliGoodEvent: () => void;
    reportSliBadEvent: () => void;
}

export const SLIContext = React.createContext<SLICtx | null>(null);

interface SLIEvents {
    [eventKey: string]: { isVisited: boolean; isGood: boolean; hasError: boolean };
}

function reportPageLoadAction(eventType: 'started' | 'success' | 'error') {
    datadogRum.addAction('SLIPageLoad', { eventType });
    if (!datadogRum.getInitConfiguration()) {
        console.debug('SLIPageLoad', { eventType });
    }
}

export function SLIContextProvider(props: {
    children: React.ReactNode;
    onSLIEvent?: (eventType: 'started' | 'success' | 'error') => void;
}) {
    const reportEvent = props.onSLIEvent ? props.onSLIEvent : reportPageLoadAction;
    const location = useLocation();
    const sliEvents = useRef<SLIEvents>({});
    const queryPath = `${location.pathname}${location.search}`;

    function isPathRelevantForSLIs(urlPath: string) {
        return urlPath.startsWith('/rides');
    }

    useEffect(() => {
        function markUrlPathAsVisited(urlPath: string) {
            const eventNotYetReported = sliEvents.current[urlPath] === undefined;
            if (eventNotYetReported) {
                sliEvents.current[urlPath] = { isVisited: true, isGood: false, hasError: false };
                reportEvent('started');
            } else if (sliEvents.current[urlPath].isGood && !sliEvents.current[urlPath].isVisited) {
                sliEvents.current[urlPath].isVisited = true;
                reportEvent('started');
            } else if (sliEvents.current[urlPath].hasError && !sliEvents.current[urlPath].isVisited) {
                sliEvents.current[urlPath].isVisited = true;
                reportEvent('started');
            }
        }

        if (isPathRelevantForSLIs(queryPath)) {
            markUrlPathAsVisited(queryPath);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queryPath]);

    const reportSliGoodEvent = useCallback(
        function reportSliEvent() {
            if (isPathRelevantForSLIs(queryPath)) {
                const eventNotYetReported = sliEvents.current[queryPath] === undefined;
                if (eventNotYetReported) {
                    sliEvents.current[queryPath] = { isVisited: false, isGood: true, hasError: false };
                    reportEvent('success');
                } else if (sliEvents.current[queryPath].isVisited && !sliEvents.current[queryPath].isGood) {
                    sliEvents.current[queryPath].isGood = true;
                    reportEvent('success');
                }
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [queryPath]
    );

    const reportSliBadEvent = useCallback(
        function reportSliEvent() {
            // Only report error if we know of a success event before (to cancel it out)
            if (isPathRelevantForSLIs(queryPath)) {
                const eventIsReported = sliEvents.current[queryPath] !== undefined;
                if (eventIsReported && !sliEvents.current[queryPath].hasError && sliEvents.current[queryPath].isGood) {
                    sliEvents.current[queryPath].hasError = true;
                    reportEvent('error');
                }
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [queryPath]
    );

    return (
        <SLIContext.Provider value={{ reportSliGoodEvent, reportSliBadEvent }}>{props.children}</SLIContext.Provider>
    );
}

class OutsideContextError extends Error {}

export function useSLIContext() {
    const context = React.useContext(SLIContext);
    if (!context) {
        throw new OutsideContextError('useSLIContext must be used within a SLIContextProvider');
    }
    return context;
}
